import React, { useMemo } from 'react';
import { MapPin, DollarSign, BarChart3 } from 'lucide-react';

const currency = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

const SCORE_STYLES = {
  green: {
    label: 'Green',
    className: 'bg-green-100 text-green-700',
  },
  yellow: {
    label: 'Yellow',
    className: 'bg-yellow-100 text-yellow-700',
  },
  red: {
    label: 'Red',
    className: 'bg-red-100 text-red-700',
  },
};

const REACTION_EMOJIS = {
  love: '❤️',
  'thumbs-up': '👍',
  'thumbs-down': '👎',
};

export default function HouseCard({ house, onSelect }) {
  const analysis = house.analysis_data || {};
  const scoreMeta = SCORE_STYLES[house.score] || SCORE_STYLES.yellow;

  const reactionSummary = useMemo(() => {
    if (!house.reactions || house.reactions.length === 0) {
      return [];
    }

    const counts = house.reactions.reduce((acc, reaction) => {
      if (!reaction.reaction) return acc;
      acc[reaction.reaction] = (acc[reaction.reaction] || 0) + 1;
      return acc;
    }, {});

    return Object.entries(counts)
      .map(([key, count]) => ({ key, count, emoji: REACTION_EMOJIS[key] || '🏠' }))
      .filter((entry) => entry.count > 0);
  }, [house.reactions]);

  const handleClick = () => {
    if (onSelect) {
      onSelect(house);
    }
  };

  return (
    <article
      onClick={handleClick}
      className="group bg-white border border-indigo-100 hover:border-indigo-200 hover:shadow-lg transition-all rounded-xl p-5 cursor-pointer"
    >
      <header className="flex items-start justify-between gap-3 mb-4">
        <div>
          <p className="flex items-center gap-2 text-sm text-indigo-500 font-medium">
            <MapPin className="w-4 h-4" />
            {house.address || 'Address TBD'}
          </p>
          {house.price && (
            <p className="text-2xl font-bold text-gray-800 mt-1">
              {currency.format(house.price)}
            </p>
          )}
        </div>
        <span className={`px-3 py-1 text-xs font-semibold rounded-full uppercase tracking-wide ${scoreMeta.className}`}>
          {scoreMeta.label}
        </span>
      </header>

      <dl className="grid grid-cols-2 gap-4 text-sm text-gray-600">
        <div>
          <dt className="flex items-center gap-1 text-xs uppercase font-semibold tracking-wide text-gray-500">
            <DollarSign className="w-3 h-3" />
            Monthly Payment
          </dt>
          <dd className="text-lg font-semibold text-gray-800">
            {analysis.totalMonthly ? currency.format(analysis.totalMonthly) : '—'}
          </dd>
        </div>
        <div>
          <dt className="flex items-center gap-1 text-xs uppercase font-semibold tracking-wide text-gray-500">
            <BarChart3 className="w-3 h-3" />
            Down Payment
          </dt>
          <dd className="text-lg font-semibold text-gray-800">
            {analysis.downPaymentAmount ? currency.format(analysis.downPaymentAmount) : '—'}
          </dd>
        </div>
      </dl>

      <footer className="mt-4 text-xs text-gray-500 flex items-center justify-between">
        <span>Saved {new Date(house.created_at).toLocaleDateString()}</span>
        {reactionSummary.length > 0 ? (
          <span className="flex items-center gap-2">
            {reactionSummary.map((entry) => (
              <span key={entry.key} className="inline-flex items-center gap-1">
                <span>{entry.emoji}</span>
                <span>{entry.count}</span>
              </span>
            ))}
          </span>
        ) : (
          <span className="opacity-0 group-hover:opacity-100 transition-opacity text-indigo-600 font-semibold">
            View details →
          </span>
        )}
      </footer>
    </article>
  );
}
